export interface CourseHomeNumberableItem {
  kind: 'component' | 'divider';
}

export interface NumberedCourseHomeItem<T extends CourseHomeNumberableItem> {
  displayNumber: number | null;
  item: T;
}

import {
  isCoachDueEntryComplete,
  type CoachDueEntryUnit,
} from '@/features/kaiCoach/kaiCoachContent';

export interface QuestionDueSoonLine {
  dueAtMillis: number;
  dueLabel: string;
  kind: 'homework' | 'preLecture';
  questionCount: number;
  typeLabel: 'Homework' | 'Pre-lecture';
}

// A Kai Coach session — or an activity with its own due date — that is open and
// still due. A coach can have several.
export interface CoachDueSoonLine {
  // Whether the coach awards credit, so a student's earned points can mark the
  // line complete.
  creditEnabled: boolean;
  dueAtMillis: number;
  dueLabel: string;
  // Unique within the coach.
  entryKey: string;
  kind: 'coachSession';
  // What the homepage row says while this is the coach's next deadline.
  rowLabel: string;
  // "Week 3", or "Week 3 · Peer response" for an activity with its own date.
  sessionLabel: string;
  // The coach thread a link opens (the `thread` query parameter).
  threadKey: string;
  units: CoachDueEntryUnit[];
}

export type DueSoonLine = QuestionDueSoonLine | CoachDueSoonLine;

export interface DueSoonSourceComponent {
  dueSoonLines?: DueSoonLine[];
  href: string;
  id: string;
  kind: 'component';
  title: string;
}

export interface DueSoonItem {
  href: string;
  id: string;
  lines: DueSoonLine[];
  title: string;
}

export function computeComponentDisplayNumbers<T extends CourseHomeNumberableItem>(
  items: T[],
  disableAutoNumbering: boolean,
): Array<NumberedCourseHomeItem<T>> {
  let nextDisplayNumber = 1;

  return items.map((item) => {
    if (disableAutoNumbering || item.kind === 'divider') {
      return {
        displayNumber: null,
        item,
      };
    }

    const displayNumber = nextDisplayNumber;
    nextDisplayNumber += 1;

    return {
      displayNumber,
      item,
    };
  });
}

export function formatNumberedTitle(
  title: string,
  displayNumber: number | null,
) {
  return displayNumber ? `${displayNumber}. ${title}` : title;
}

export function buildDueSoonItems<T extends CourseHomeNumberableItem>(
  numberedItems: Array<NumberedCourseHomeItem<T>>,
  disableDueSoonSection: boolean,
): DueSoonItem[] {
  if (disableDueSoonSection) {
    return [];
  }

  return numberedItems.flatMap(({ displayNumber, item }) => {
    if (item.kind !== 'component') {
      return [];
    }

    const component = item as T & DueSoonSourceComponent;
    const lines = component.dueSoonLines?.filter(Boolean) ?? [];

    if (lines.length === 0) {
      return [];
    }

    return [
      {
        href: component.href,
        id: component.id,
        lines,
        title: formatNumberedTitle(component.title, displayNumber),
      },
    ];
  });
}

// What applyCoachDueProgress reads and rewrites on a homepage item.
interface CoachProgressItem extends CourseHomeNumberableItem {
  dueLabel?: string;
  dueSoonLines?: DueSoonLine[];
  id: string;
}

/**
 * A student's view of the coach deadlines on the homepage: a coach line whose
 * units the student has fully earned is complete, so it leaves Due Soon, and a
 * coach's row names the next deadline they have not met (or none). Coaches
 * without progress in the map — staff, a pending or failed request, no credit —
 * are left exactly as they are.
 */
export function applyCoachDueProgress<T extends CoachProgressItem>(
  items: T[],
  progress: Record<string, Record<string, number>>,
): T[] {
  return items.map((item) => {
    const earned = progress[item.id];
    const coachLines = (item.dueSoonLines ?? []).filter(
      (line): line is CoachDueSoonLine => line.kind === 'coachSession',
    );
    if (item.kind !== 'component' || !earned || coachLines.length === 0) {
      return item;
    }
    const isComplete = (line: CoachDueSoonLine) =>
      line.creditEnabled && isCoachDueEntryComplete(line, earned);
    const lines = (item.dueSoonLines ?? []).filter(
      (line) => line.kind !== 'coachSession' || !isComplete(line),
    );
    // Only a label that named a coach deadline moves; a homework label stays.
    const labelWasCoach = coachLines.some((line) => line.rowLabel === item.dueLabel);
    const nextCoachLine = coachLines.find((line) => !isComplete(line));
    return {
      ...item,
      dueSoonLines: lines,
      ...(labelWasCoach ? { dueLabel: nextCoachLine?.rowLabel } : {}),
    };
  });
}

export function reorderCourseHomeItems<T>(items: T[], fromIndex: number, toIndex: number) {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);

  if (movedItem === undefined) {
    return items;
  }

  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
}
